import React, { useState, useEffect, useRef } from 'react';
import { Paper, Button, Box, Typography, Slider, ButtonGroup } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SaveIcon from '@mui/icons-material/Save';
import BrushIcon from '@mui/icons-material/Brush';
import ImageIcon from '@mui/icons-material/Image';
import { useSelector } from 'react-redux';

/**
 * GlassOverImageAdvanced - Advanced drawing implementation with Fabric.js-like features
 * Note: This is a pure React implementation without external Fabric.js dependency
 * Includes: Multiple colors, brush sizes, undo/redo, and advanced controls
 * 
 * OPTIMIZATION: Images are stored centrally in IndexedDB at DBGroup level
 * Field values only contain drawing paths (lines) without image data
 */
const GlassOverImageAdvanced = ({ id, properties, value, valueUpdaterCallback, validityUpdaterCallback, fieldContext }) => {
    const { options = {} } = properties;
    const language = useSelector(state => state.App_Settings.language);
    const label = options.label || 'Draw Over Image';
    
    // Extract context for image storage
    const dbGroupName = fieldContext?.dbGroupName;
    const fieldName = fieldContext?.fieldName;
    
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentLine, setCurrentLine] = useState([]);
    const [lines, setLines] = useState([]);
    const [history, setHistory] = useState([]);
    const [historyStep, setHistoryStep] = useState(-1);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    
    // Drawing settings
    const [brushColor, setBrushColor] = useState('#FF0000');
    const [brushSize, setBrushSize] = useState(3);
    const [drawingMode, setDrawingMode] = useState('pen'); // pen, eraser

    const colors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#000000', '#FFFFFF'];

    /**
     * Save image to IndexedDB at DBGroup level
     * Storage path: DBgroups -> {dbGroupName} -> fieldImages -> {fieldName}
     */
    const saveImageToDBGroup = async (imageDataUrl) => {
        if (!dbGroupName || !fieldName) {
            console.warn('[GlassOverImageAdvanced] Cannot save image: missing dbGroupName or fieldName');
            return false;
        }

        try {
            // Open without version to avoid version conflicts
            const dbRequest = indexedDB.open('ShepherdDB');
            
            return new Promise((resolve, reject) => {
                dbRequest.onsuccess = (event) => {
                    const db = event.target.result;
                    const transaction = db.transaction(['DBgroups'], 'readwrite');
                    const store = transaction.objectStore('DBgroups');
                    const nameIndex = store.index('name');
                    const getRequest = nameIndex.get(dbGroupName);
                    
                    getRequest.onsuccess = () => {
                        const dbGroup = getRequest.result;
                        if (!dbGroup) {
                            console.error('[GlassOverImageAdvanced] DBGroup not found:', dbGroupName);
                            db.close();
                            reject(new Error('DBGroup not found'));
                            return;
                        }
                        
                        // Initialize fieldImages object if it doesn't exist
                        if (!dbGroup.fieldImages) {
                            dbGroup.fieldImages = {};
                        }
                        
                        // Store image under field name
                        dbGroup.fieldImages[fieldName] = imageDataUrl;
                        
                        // Update the DBGroup
                        const updateRequest = store.put(dbGroup);
                        
                        updateRequest.onsuccess = () => {
                            console.log('[GlassOverImageAdvanced] Image saved to DBGroup:', dbGroupName, 'field:', fieldName);
                            db.close();
                            resolve(true);
                        };
                        
                        updateRequest.onerror = () => {
                            console.error('[GlassOverImageAdvanced] Error updating DBGroup');
                            db.close();
                            reject(new Error('Error updating DBGroup'));
                        };
                    };
                    
                    getRequest.onerror = () => {
                        console.error('[GlassOverImageAdvanced] Error retrieving DBGroup');
                        db.close();
                        reject(new Error('Error retrieving DBGroup'));
                    };
                };
                
                dbRequest.onerror = () => {
                    console.error('[GlassOverImageAdvanced] Error opening IndexedDB');
                    reject(new Error('Error opening IndexedDB'));
                };
            });
        } catch (error) {
            console.error('[GlassOverImageAdvanced] Exception saving image:', error);
            return false;
        }
    };

    /**
     * Load image from IndexedDB at DBGroup level
     * Storage path: DBgroups -> {dbGroupName} -> fieldImages -> {fieldName}
     */
    const loadImageFromDBGroup = async () => {
        if (!dbGroupName || !fieldName) {
            console.warn('[GlassOverImageAdvanced] Cannot load image: missing dbGroupName or fieldName');
            return null;
        }

        try {
            // Open without version to avoid version conflicts
            const dbRequest = indexedDB.open('ShepherdDB');
            
            return new Promise((resolve, reject) => {
                dbRequest.onsuccess = (event) => {
                    const db = event.target.result;
                    const transaction = db.transaction(['DBgroups'], 'readonly');
                    const store = transaction.objectStore('DBgroups');
                    const nameIndex = store.index('name');
                    const getRequest = nameIndex.get(dbGroupName);
                    
                    getRequest.onsuccess = () => {
                        const dbGroup = getRequest.result;
                        db.close();
                        
                        if (!dbGroup) {
                            console.warn('[GlassOverImageAdvanced] DBGroup not found:', dbGroupName);
                            resolve(null);
                            return;
                        }
                        
                        // Retrieve image from fieldImages
                        const imageData = dbGroup.fieldImages?.[fieldName];
                        if (imageData) {
                            console.log('[GlassOverImageAdvanced] Image loaded from DBGroup:', dbGroupName, 'field:', fieldName);
                        } else {
                            console.log('[GlassOverImageAdvanced] No image found in DBGroup for field:', fieldName);
                        }
                        resolve(imageData || null);
                    };
                    
                    getRequest.onerror = () => {
                        console.error('[GlassOverImageAdvanced] Error retrieving DBGroup');
                        db.close();
                        reject(new Error('Error retrieving DBGroup'));
                    };
                };
                
                dbRequest.onerror = () => {
                    console.error('[GlassOverImageAdvanced] Error opening IndexedDB');
                    reject(new Error('Error opening IndexedDB'));
                };
            });
        } catch (error) {
            console.error('[GlassOverImageAdvanced] Exception loading image:', error);
            return null;
        }
    };

    // Load image from DBGroup on mount and sync with parent value
    useEffect(() => {
        const initializeComponent = async () => {
            // Load drawing lines from value
            if (value && value.lines && lines.length === 0 && history.length === 0) {
                const newLines = value.lines;
                setLines(newLines);
                setHistory([newLines]);
                setHistoryStep(0);
            }
            
            // Handle image loading with priority:
            // 1. If value has imageData (backward compatibility), use it
            // 2. Otherwise, try to load from DBGroup
            if (value && value.imageData) {
                // Backward compatibility: use embedded image data
                console.log('[GlassOverImageAdvanced] Using embedded imageData (backward compatibility)');
                setSelectedImage(value.imageData);
            } else if (dbGroupName && fieldName) {
                // New approach: load image from DBGroup
                const imageData = await loadImageFromDBGroup();
                if (imageData) {
                    setSelectedImage(imageData);
                }
            }
        };
        
        initializeComponent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, lines.length, history.length, dbGroupName, fieldName]);

    // Update parent whenever lines change (NO imageData stored)
    useEffect(() => {
        if (imageLoaded || lines.length > 0) {
            const drawingData = {
                lines: lines,
                // imageData is NOT included - image is stored at DBGroup level
                settings: {
                    brushColor,
                    brushSize,
                },
                timestamp: new Date().toISOString(),
            };
            valueUpdaterCallback(drawingData);
            validityUpdaterCallback(lines.length > 0 && selectedImage !== null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines, imageLoaded]);

    // Redraw canvas when lines change
    useEffect(() => {
        if (imageLoaded && canvasRef.current) {
            redrawCanvas();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines, imageLoaded]);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const imageDataUrl = e.target.result;
                setSelectedImage(imageDataUrl);
                setImageLoaded(false); // Reset to false, will be set true after canvas is ready
                
                // Save image to DBGroup storage (not in field value)
                if (dbGroupName && fieldName) {
                    const saved = await saveImageToDBGroup(imageDataUrl);
                    if (saved) {
                        console.log('[GlassOverImageAdvanced] Image saved to DBGroup successfully');
                    } else {
                        console.warn('[GlassOverImageAdvanced] Failed to save image to DBGroup');
                    }
                } else {
                    console.warn('[GlassOverImageAdvanced] Cannot save to DBGroup: missing context');
                }
            };
            reader.readAsDataURL(file);
        }
    };

    // Initialize canvas when image is selected
    useEffect(() => {
        if (selectedImage && canvasRef.current && !imageLoaded) {
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                if (canvas) {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    setImageLoaded(true);
                }
            };
            img.src = selectedImage;
        }
    }, [selectedImage, imageLoaded]);

    const getCoordinates = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        let clientX, clientY;

        if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        };
    };

    const startDrawing = (event) => {
        event.preventDefault();
        setIsDrawing(true);
        const coords = getCoordinates(event);
        setCurrentLine([coords]);
    };

    const draw = (event) => {
        if (!isDrawing) return;
        event.preventDefault();
        
        const coords = getCoordinates(event);
        const newLine = [...currentLine, coords];
        setCurrentLine(newLine);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        if (currentLine.length > 0) {
            // Don't modify the canvas during eraser mode - we'll handle it on stopDrawing
            if (drawingMode !== 'eraser') {
                ctx.globalCompositeOperation = 'source-over';
                ctx.strokeStyle = brushColor;
                ctx.lineWidth = brushSize;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                
                ctx.beginPath();
                const lastPoint = currentLine[currentLine.length - 1];
                ctx.moveTo(lastPoint.x, lastPoint.y);
                ctx.lineTo(coords.x, coords.y);
                ctx.stroke();
            } else {
                // Draw a temporary visual indicator for eraser
                ctx.globalCompositeOperation = 'source-over';
                ctx.strokeStyle = 'rgba(255, 0, 0, 0.3)';
                ctx.lineWidth = brushSize;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                
                ctx.beginPath();
                const lastPoint = currentLine[currentLine.length - 1];
                ctx.moveTo(lastPoint.x, lastPoint.y);
                ctx.lineTo(coords.x, coords.y);
                ctx.stroke();
            }
        }
    };

    const stopDrawing = () => {
        if (isDrawing && currentLine.length > 0) {
            if (drawingMode === 'eraser') {
                // Eraser mode: Remove lines that intersect with the eraser path
                const eraserPath = currentLine;
                const remainingLines = lines.filter(line => !lineIntersectsPath(line.points, eraserPath, brushSize));
                setLines(remainingLines);
                
                // Update history
                const newHistory = history.slice(0, historyStep + 1);
                newHistory.push(remainingLines);
                setHistory(newHistory);
                setHistoryStep(newHistory.length - 1);
            } else {
                // Drawing mode: Add new line
                const newLineObject = {
                    points: currentLine,
                    color: brushColor,
                    width: brushSize,
                    mode: drawingMode,
                    timestamp: Date.now(),
                };
                
                const newLines = [...lines, newLineObject];
                setLines(newLines);
                
                // Update history
                const newHistory = history.slice(0, historyStep + 1);
                newHistory.push(newLines);
                setHistory(newHistory);
                setHistoryStep(newHistory.length - 1);
            }
            
            setCurrentLine([]);
        }
        setIsDrawing(false);
    };

    // Helper function to check if a line intersects with the eraser path
    const lineIntersectsPath = (linePoints, eraserPath, eraserSize) => {
        // Check if any point in the line is within eraser radius of any point in eraser path
        const eraserRadius = eraserSize / 2;
        
        for (let i = 0; i < linePoints.length; i++) {
            const linePoint = linePoints[i];
            for (let j = 0; j < eraserPath.length; j++) {
                const eraserPoint = eraserPath[j];
                const distance = Math.sqrt(
                    Math.pow(linePoint.x - eraserPoint.x, 2) + 
                    Math.pow(linePoint.y - eraserPoint.y, 2)
                );
                if (distance <= eraserRadius) {
                    return true;
                }
            }
        }
        return false;
    };

    const redrawCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        if (selectedImage) {
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
                
                lines.forEach((line) => {
                    if (line.points && line.points.length > 1) {
                        ctx.globalCompositeOperation = 'source-over';
                        ctx.strokeStyle = line.color;
                        ctx.lineWidth = line.width;
                        ctx.lineCap = 'round';
                        ctx.lineJoin = 'round';
                        
                        ctx.beginPath();
                        ctx.moveTo(line.points[0].x, line.points[0].y);
                        for (let i = 1; i < line.points.length; i++) {
                            ctx.lineTo(line.points[i].x, line.points[i].y);
                        }
                        ctx.stroke();
                    }
                });
                
                ctx.globalCompositeOperation = 'source-over';
            };
            img.src = selectedImage;
        }
    };

    const handleUndo = () => {
        if (historyStep > 0) {
            const newStep = historyStep - 1;
            setHistoryStep(newStep);
            setLines(history[newStep]);
        }
    };

    const handleRedo = () => {
        if (historyStep < history.length - 1) {
            const newStep = historyStep + 1;
            setHistoryStep(newStep);
            setLines(history[newStep]);
        }
    };

    const handleClear = () => {
        const newLines = [];
        setLines(newLines);
        const newHistory = [...history, newLines];
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);
    };

    const exportDrawing = () => {
        const drawingData = {
            lines: lines,
            // imageData is NOT included - stored at DBGroup level
            // Image reference is implicit via dbGroupName + fieldName
            settings: {
                brushColor,
                brushSize,
                drawingMode,
            },
            metadata: {
                timestamp: new Date().toISOString(),
                totalLines: lines.length,
                totalPoints: lines.reduce((sum, line) => sum + line.points.length, 0),
                historySteps: history.length,
                dbGroupName: dbGroupName || 'unknown',
                fieldName: fieldName || 'unknown',
            }
        };
        
        // Log to console
        console.log('Drawing Data (JSON - Advanced, Optimized):', JSON.stringify(drawingData, null, 2));
        
        // Create downloadable JSON file
        const jsonString = JSON.stringify(drawingData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `drawing-advanced-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log('Exported JSON file successfully (without image data)');
        
        return drawingData;
    };

    const exportAsImage = () => {
        if (!canvasRef.current) return;
        
        const canvas = canvasRef.current;
        
        // Convert canvas to blob and download
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `drawing-image-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            console.log('Exported image successfully');
        }, 'image/png');
    };

    return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: '#fff3e0', width: '100%', maxWidth: '100%' }}>
            <Typography variant="h6" gutterBottom>
                {label} (Advanced - Optimized)
            </Typography>
            {dbGroupName && fieldName && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
                    💾 Image stored centrally in {dbGroupName} group • Only paths saved in record
                </Typography>
            )}

            {!selectedImage ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Button variant="contained" component="label" sx={{ bgcolor: '#ff9800' }}>
                        Upload Image
                        <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleImageUpload}
                        />
                    </Button>
                </Box>
            ) : (
                <Box>
                    {/* Drawing Controls */}
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<UndoIcon />}
                                onClick={handleUndo}
                                disabled={historyStep <= 0}
                            >
                                Undo
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RedoIcon />}
                                onClick={handleRedo}
                                disabled={historyStep >= history.length - 1}
                            >
                                Redo
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleClear}
                                disabled={lines.length === 0}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{ bgcolor: '#ff9800' }}
                                startIcon={<SaveIcon />}
                                onClick={exportDrawing}
                            >
                                Export JSON
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{ bgcolor: '#4caf50' }}
                                startIcon={<ImageIcon />}
                                onClick={exportAsImage}
                            >
                                Export Image
                            </Button>
                        </Box>

                        {/* Mode Selection */}
                        <Box sx={{ mb: 2 }}>
                            <ButtonGroup size="small" variant="contained">
                                <Button
                                    onClick={() => setDrawingMode('pen')}
                                    sx={{ 
                                        bgcolor: drawingMode === 'pen' ? '#ff9800' : 'grey.300',
                                        '&:hover': { bgcolor: '#fb8c00' }
                                    }}
                                >
                                    <BrushIcon /> Pen
                                </Button>
                                <Button
                                    onClick={() => setDrawingMode('eraser')}
                                    sx={{ 
                                        bgcolor: drawingMode === 'eraser' ? '#ff9800' : 'grey.300',
                                        '&:hover': { bgcolor: '#fb8c00' }
                                    }}
                                >
                                    Eraser
                                </Button>
                            </ButtonGroup>
                        </Box>

                        {/* Color Palette */}
                        {drawingMode === 'pen' && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    Color:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {colors.map((color) => (
                                        <Box
                                            key={color}
                                            onClick={() => setBrushColor(color)}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: color,
                                                border: brushColor === color ? '3px solid #000' : '1px solid #ccc',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Brush Size */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" display="block" gutterBottom>
                                Brush Size: {brushSize}px
                            </Typography>
                            <Slider
                                value={brushSize}
                                onChange={(e, val) => setBrushSize(val)}
                                min={1}
                                max={20}
                                step={1}
                                sx={{ 
                                    width: 200,
                                    color: '#ff9800'
                                }}
                            />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            position: 'relative',
                            border: '2px solid #ff9800',
                            borderRadius: 1,
                            overflow: 'hidden',
                            maxWidth: '100%',
                            touchAction: 'none',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <canvas
                            ref={canvasRef}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                            style={{
                                display: 'block',
                                maxWidth: '100%',
                                height: 'auto',
                                cursor: drawingMode === 'eraser' ? 'crosshair' : 'crosshair',
                            }}
                        />
                    </Box>

                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#e65100' }}>
                        Lines: {lines.length} | History: {historyStep + 1}/{history.length} | Mode: {drawingMode}
                        {dbGroupName && fieldName && (
                            <><br />📁 Image stored in: {dbGroupName}/{fieldName} (Optimized)</>
                        )}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default GlassOverImageAdvanced;
